/**
 * src/lib/api/tasks.js — Task read/write API.
 * Returns raw snake_case Supabase rows. See docs/SHAPE_CONTRACT.md.
 */

import { supabase } from '../supabase';
import { indexKnowledge } from './knowledge-indexer';
import { logActivity } from './activityLog';
import { normalizeTaskStatus } from '../constants/taskStatus';
import { normalizeDepartment } from '../constants/departments';
import { createBundle } from './bundles';

// Night Watch 2026-09-29 (P3, defense-in-depth): dealId is interpolated into a
// PostgREST .or() filter in reassignDealBundlesToProject (TICKET-977 S2), so a
// value carrying filter metacharacters could broaden an UPDATE's row match.
// A local const is the accepted repo pattern (no cross-layer import).
const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * TICKET-070 Step 4 ("execute & report") — task-flag whitelist.
 * The two columns (is_field_log, out_of_scope) are whitelisted UNCONDITIONALLY:
 * the migration is APPLIED live so the columns always exist. The fields are only
 * EVER present in a payload when the Step-4 UI is enabled
 * (isStep4ShopExecuteEnabled, see src/lib/featureFlags.js), so a gate here is
 * redundant — and the previous `process.env.STEP4_SHOP_EXECUTE_V1` gate was a
 * BUG in the browser (process.env is undefined → the flags were silently dropped
 * even when the UI sent them). Booleans are coerced (=== true). Mirrors
 * applyStep4TaskFlags in lib/handlers/taskAuthorizations.js.
 */
function applyStep4TaskFlags(target, src = {}) {
  if (src.is_field_log !== undefined) target.is_field_log = src.is_field_log === true;
  if (src.out_of_scope !== undefined) target.out_of_scope = src.out_of_scope === true;
  return target;
}

const SELECT_TASK = '*, user_profiles!assignee_id(display_name), projects!project_id(project_name, vessel_id), stages!stage_id(stage_name, label_en, label_es, color)';

export async function fetchTasks(filters = {}) {
  try {
    let query = supabase
      .from('tasks')
      .select(SELECT_TASK)
      .order('due_date', { ascending: true, nullsFirst: false });

    if (filters.project_id) query = query.eq('project_id', filters.project_id);
    if (filters.assignee_id) query = query.eq('assignee_id', filters.assignee_id);
    if (filters.department) {
      const dept = normalizeDepartment(filters.department) || filters.department;
      query = query.eq('department', dept);
    }
    if (filters.status) {
      query = query.eq('status', normalizeTaskStatus(filters.status));
    }

    const { data, error } = await query;
    if (error) { console.warn('[tasks] fetch error:', error.message); return []; }
    return data || [];
  } catch (err) {
    console.warn('[tasks] fetchTasks failed:', err.message);
    return [];
  }
}

/**
 * TICKET-067 Phase B — load the photo-mark tasks for a given photo: tasks whose
 * photo_document_id matches AND that carry a non-null region (a mark drawn on the
 * image). Whole-photo tasks without a region are excluded — they aren't marks.
 * Archived tasks are excluded (soft-archive contract). Returns raw rows.
 *
 * @param {string} photoDocumentId — project_documents.id of the open photo
 * @returns {Promise<Array>} tasks-with-region for this photo
 */
export async function fetchTasksByPhoto(photoDocumentId) {
  if (!photoDocumentId) return [];
  try {
    const { data, error } = await supabase
      .from('tasks')
      .select(SELECT_TASK)
      .eq('photo_document_id', photoDocumentId)
      .not('region', 'is', null)
      .is('archived_at', null)
      .order('created_at', { ascending: true, nullsFirst: true });
    if (error) { console.warn('[tasks] fetchTasksByPhoto error:', error.message); return []; }
    return data || [];
  } catch (err) {
    console.warn('[tasks] fetchTasksByPhoto failed:', err.message);
    return [];
  }
}

/**
 * TICKET-216c — the annotated task-photos for a BUNDLE (mirrors the service
 * order). Tasks of this bundle that hang off a photo (photo_document_id) AND
 * carry a region (a mark drawn on the image). Whole-photo / region-less tasks
 * are excluded. Lets the quote render the same image+annotation crops the SO
 * shows, with NO migration (quote_line.bundle_id -> tasks -> photo+region).
 *
 * @param {string} bundleId — project_bundles.id
 * @returns {Promise<Array>} tasks-with-region+photo for this bundle
 */
export async function fetchTasksByBundle(bundleId) {
  if (!bundleId) return [];
  try {
    const { data, error } = await supabase
      .from('tasks')
      .select(SELECT_TASK)
      .eq('bundle_id', bundleId)
      .not('photo_document_id', 'is', null)
      .not('region', 'is', null)
      .is('archived_at', null)
      .order('created_at', { ascending: true, nullsFirst: true });
    if (error) { console.warn('[tasks] fetchTasksByBundle error:', error.message); return []; }
    return data || [];
  } catch (err) {
    // spine-emit-ok: best-effort read; warns + returns [] so callers degrade
    console.warn('[tasks] fetchTasksByBundle failed:', err.message);
    return [];
  }
}

export async function createTask(payload) {
  try {
    const row = {
      task_name: payload.task_name,
      status: normalizeTaskStatus(payload.status || 'todo'),
    };
    // Offline sync: accept a client-generated uuid so an offline create and its
    // offline edits share one stable id, and replay is idempotent (PK conflict
    // on a row already synced). Online creates omit id → server default fills it.
    if (payload.id) row.id = payload.id;
    if (payload.department) row.department = normalizeDepartment(payload.department) || payload.department;
    if (payload.department_id) row.department_id = payload.department_id;
    if (payload.assignee_id) row.assignee_id = payload.assignee_id;
    if (payload.due_date || payload.due) row.due_date = payload.due_date || payload.due;
    if (payload.start_date || payload.start) row.start_date = payload.start_date || payload.start;
    if (payload.project_id) row.project_id = payload.project_id;
    if (payload.bundle_id) row.bundle_id = payload.bundle_id;
    if (payload.stage_id) row.stage_id = payload.stage_id;
    if (payload.notes) row.notes = payload.notes;
    if (payload.priority) row.priority = payload.priority;
    if (payload.estimated_hours != null) {
      const parsed = parseFloat(payload.estimated_hours);
      if (!isNaN(parsed)) row.estimated_hours = parsed;
    }
    // TICKET-067 Phase B — a photo mark IS a task: the task hangs off a photo
    // (photo_document_id) carrying a normalized region shape, and may anchor to a
    // pipeline deal (deal_id) before a project exists. work_tag is the defect/work
    // category surfaced on the service order. region is stored as-is (validated by
    // src/lib/photos/regionShapes.js before it reaches here).
    if (payload.photo_document_id) row.photo_document_id = payload.photo_document_id;
    if (payload.region !== undefined) row.region = payload.region;
    if (payload.deal_id) row.deal_id = payload.deal_id;
    if (payload.work_tag !== undefined) row.work_tag = payload.work_tag || null;
    // TICKET-070 Step 4 — field-log / out-of-scope flags (gated, dormant by default).
    applyStep4TaskFlags(row, payload);

    const { data, error } = await supabase
      .from('tasks')
      .insert(row)
      .select(SELECT_TASK)
      .single();
    if (error) return { success: false, error: error.message };

    const projectName = data.projects?.project_name || '';
    const assigneeName = data.user_profiles?.display_name || '';
    const text = `[New Task] ${data.task_name}${projectName ? ` | Project: ${projectName}` : ''}${assigneeName ? ` | Assigned: ${assigneeName}` : ''}${payload.priority ? ` | Priority: ${payload.priority}` : ''}`;
    indexKnowledge(text, projectName || 'Tasks');

    logActivity({ entityType: 'task', entityId: data.id, entityName: data.task_name, action: 'created' });

    return { success: true, record: data };
  } catch (err) { return { success: false, error: err.message }; }
}

export async function updateTask(payload) {
  try {
    const { record_id, ...rest } = payload;
    if (!record_id) return { success: false, error: 'record_id is required' };

    const updates = {};
    if (rest.task_name !== undefined) updates.task_name = rest.task_name;
    if (rest.status !== undefined) updates.status = normalizeTaskStatus(rest.status);
    if (rest.department !== undefined) {
      updates.department = rest.department ? (normalizeDepartment(rest.department) || rest.department) : null;
    }
    if (rest.department_id !== undefined) updates.department_id = rest.department_id || null;
    if (rest.assignee_id !== undefined) updates.assignee_id = rest.assignee_id || null;
    if (rest.due !== undefined || rest.due_date !== undefined) {
      updates.due_date = (rest.due_date ?? rest.due) || null;
    }
    if (rest.start !== undefined || rest.start_date !== undefined) {
      updates.start_date = (rest.start_date ?? rest.start) || null;
    }
    if (rest.project_id !== undefined) updates.project_id = rest.project_id || null;
    if (rest.bundle_id !== undefined) updates.bundle_id = rest.bundle_id || null;
    if (rest.stage_id !== undefined) updates.stage_id = rest.stage_id || null;
    if (rest.priority !== undefined) updates.priority = rest.priority || null;
    if (rest.notes !== undefined) updates.notes = rest.notes || '';
    // TICKET-372 — the two CUSTOMER-FACING fields on a photo mark. This mapper is
    // an explicit allow-list, so a field missing here is silently dropped on save.
    // NULL (not '') when cleared: the quote publishes a mark only when one of
    // these is non-null, so an empty string would publish a blank row.
    if (rest.client_label !== undefined) updates.client_label = rest.client_label || null;
    if (rest.client_note !== undefined) updates.client_note = rest.client_note || null;
    if (rest.estimated_hours !== undefined) {
      updates.estimated_hours = rest.estimated_hours === null ? null : parseFloat(rest.estimated_hours) || null;
    }
    if (rest.task_order !== undefined) {
      const parsed = parseInt(rest.task_order, 10);
      updates.task_order = isNaN(parsed) ? null : parsed;
    }
    // TICKET-067 Phase B — photo-mark fields (see createTask). region/work_tag are
    // editable (resize a mark, re-tag it); photo_document_id/deal_id are anchors.
    if (rest.photo_document_id !== undefined) updates.photo_document_id = rest.photo_document_id || null;
    if (rest.region !== undefined) updates.region = rest.region;
    if (rest.deal_id !== undefined) updates.deal_id = rest.deal_id || null;
    if (rest.work_tag !== undefined) updates.work_tag = rest.work_tag || null;
    // TICKET-070 Step 4 — field-log / out-of-scope flags (gated, dormant by default).
    applyStep4TaskFlags(updates, rest);

    if (Object.keys(updates).length === 0) return { success: true, noop: true };

    updates.updated_at = new Date().toISOString();
    const { data, error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('id', record_id)
      .select(SELECT_TASK)
      .single();
    if (error) return { success: false, error: error.message };
    return { success: true, record: data };
  } catch (err) { return { success: false, error: err.message }; }
}

export async function updateTaskStatus(recordId, status) {
  const slug = normalizeTaskStatus(status);
  const { data, error } = await supabase
    .from('tasks')
    .update({ status: slug, updated_at: new Date().toISOString() })
    .eq('id', recordId)
    .select(SELECT_TASK)
    .single();

  if (error) throw new Error(error.message);

  logActivity({ entityType: 'task', entityId: recordId, entityName: data?.task_name, action: 'status_changed', newValue: slug });

  if (slug === 'done') {
    const taskName = data?.task_name || '';
    indexKnowledge(`[Task Completed] ${taskName} | Status: ${slug}`, 'Tasks');
  }

  return { success: true, record: data };
}

/**
 * TICKET-030: worker accepts a task. Sets accepted_at + accepted_by on the
 * task row (read-receipt timestamp) and marks any unread assignment
 * notifications for this task as read for the current user.
 */
export async function acceptTask(taskId) {
  if (!taskId) return { success: false, error: 'taskId is required' };
  try {
    const { data: session } = await supabase.auth.getSession();
    const userId = session?.session?.user?.id;
    if (!userId) return { success: false, error: 'no auth session' };

    const nowIso = new Date().toISOString();
    const { data, error } = await supabase
      .from('tasks')
      .update({ accepted_at: nowIso, accepted_by: userId, updated_at: nowIso })
      .eq('id', taskId)
      .is('accepted_at', null)
      .select(SELECT_TASK)
      .single();
    if (error) return { success: false, error: error.message };

    // Best-effort: clear matching notifications so the banner shrinks. A
    // failure here is non-blocking — the task is already accepted.
    supabase
      .from('notifications')
      .update({ read_at: nowIso })
      .eq('user_id', userId)
      .eq('task_id', taskId)
      .is('read_at', null)
      .then(() => {})
      .catch(() => {});

    return { success: true, record: data };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/** TICKET-1098: the banner needs a task's work_tag to route a quote-review
 * assignment straight to its quote. Throws on error so the caller keeps the
 * notification unread rather than losing it. */
export async function fetchTaskWorkTag(taskId) {
  if (!taskId) return null;
  const { data, error } = await supabase.from('tasks').select('work_tag').eq('id', taskId).maybeSingle();
  if (error) throw new Error(error.message);
  return data?.work_tag ?? null;
}

export async function updateTaskAssignee(recordId, assigneeId) {
  try {
    // Route through the reassign_task RPC so ANY teammate can assign a task to anyone
    // (CEO 2026-07-02 "assign tickets to each other"). A direct UPDATE is RLS-blocked
    // for non-exec roles (rls_tasks_write_assignee = own tasks only); the SECURITY
    // DEFINER RPC touches ONLY assignee_id, preserving the edit/delete rules.
    // Migration: 20260702160000_tasks_assign_to_anyone.sql.
    const { error } = await supabase.rpc('reassign_task', {
      p_task_id: recordId,
      p_assignee_id: assigneeId || null,
    });
    if (error) return { success: false, error: error.message };
    // Read back the updated row (SELECT is open to all authenticated).
    const { data } = await supabase.from('tasks').select(SELECT_TASK).eq('id', recordId).single();
    return { success: true, record: data };
  } catch (err) { return { success: false, error: err.message }; }
}

/**
 * Lifecycle Slice 2: when a deal is sold and becomes a project, carry the deal's
 * tasks into the project. Sets project_id on the deal's tasks that don't yet have
 * one; deal_id (trace) and bundle_id (bundle membership) are preserved. Only
 * non-archived tasks. RLS: tasks write is ceo/admin/pmo (the converter).
 */
export async function reassignDealTasksToProject(dealId, projectId) {
  if (!dealId || !projectId) return { success: false, error: 'missing dealId/projectId' };
  try {
    const { data, error } = await supabase
      .from('tasks')
      .update({ project_id: projectId, updated_at: new Date().toISOString() })
      .eq('deal_id', dealId)
      .is('project_id', null)
      .is('archived_at', null)
      .select('id');
    if (error) return { success: false, error: error.message };
    return { success: true, count: data?.length || 0 };
  } catch (err) {
    // spine-emit-ok: returns {success:false,error} for the caller (won-promotion)
    // to surface/handle — same structured-error convention as the rest of tasks.js.
    return { success: false, error: err.message };
  }
}

/**
 * TICKET-PIPELINE-TRACE S3 (sibling of reassignDealTasksToProject): carry the
 * deal's BUNDLES into the project on Won-promotion. Matches the deal by
 * crm_deal_id OR deal_fk (TICKET-977 S2); set BOTH project_id
 * (text) and project_fk (uuid) so consumers resolving `project_fk ?? project_id`
 * see the link. Idempotent — only the deal's not-yet-projected, non-archived
 * bundles move; crm_deal_id (trace) is preserved.
 */
export async function reassignDealBundlesToProject(dealId, projectId) {
  if (!dealId || !projectId) return { success: false, error: 'missing dealId/projectId' };
  // Night Watch 2026-09-29: dealId is interpolated into the .or() filter below; a
  // non-UUID value could broaden the UPDATE's match. Guard the format.
  if (!UUID_RX.test(String(dealId))) return { success: false, error: 'invalid dealId' };
  try {
    // TICKET-977 S2: a moved bundle with no stage takes the project's stage — the
    // insert trigger (trg_bundle_inherit_project_stage) does not fire on UPDATE.
    const { data: proj, error: pErr } = await supabase
      .from('projects').select('stage_id').eq('id', projectId).maybeSingle();
    if (pErr) return { success: false, error: pErr.message };
    // Match EITHER deal key (some bundles carry only deal_fk, others only crm_deal_id).
    const move = (extra) => supabase
      .from('project_bundles')
      .update({ project_id: projectId, project_fk: projectId, updated_at: new Date().toISOString(), ...extra })
      .or(`crm_deal_id.eq.${dealId},deal_fk.eq.${dealId}`)
      .is('project_id', null)
      .is('archived_at', null);
    let count = 0;
    if (proj?.stage_id) {
      const { data, error } = await move({ stage_id: proj.stage_id }).is('stage_id', null).select('id');
      if (error) return { success: false, error: error.message };
      count += data?.length || 0;
    }
    // The rest (own stage kept, or the project has none). Already-moved rows no longer match project_id IS NULL.
    const { data, error } = await move({}).select('id');
    if (error) return { success: false, error: error.message };
    return { success: true, count: count + (data?.length || 0) };
  } catch (err) {
    // spine-emit-ok: returns {success:false,error} for the caller (won-promotion)
    // to surface/handle — same structured-error convention as the rest of tasks.js.
    return { success: false, error: err.message };
  }
}

/**
 * Classify S2: promote a task into a NEW bundle (voice-classifier fix — a task
 * that was really a bundle). Creates the bundle scoped to the task's project
 * (or its pipeline deal), then KEEPS the task as a member of that bundle
 * (bundle_id), per CEO choice 2026-06-20. Returns the new bundle id.
 */
export async function promoteTaskToBundle(task) {
  if (!task?.id) return { success: false, error: 'no task' };
  const host = task.project_id
    ? { project_id: task.project_id }
    : (task.deal_id ? { crm_deal_id: task.deal_id } : null);
  if (!host) return { success: false, error: 'task needs a project or deal before it can become a bundle' };
  try {
    const created = await createBundle({ ...host, title: task.task_name, origin: 'planned' });
    const bundleId = created?.bundle?.id;
    if (!created?.success || !bundleId) {
      return { success: false, error: created?.error || 'bundle create failed' };
    }
    // Keep the task, linked as a member of the new bundle.
    const linked = await updateTask({ record_id: task.id, bundle_id: bundleId });
    if (linked?.success === false) return { success: false, error: linked.error, bundle_id: bundleId };
    return { success: true, bundle_id: bundleId, bundle: created.bundle };
  } catch (err) {
    // spine-emit-ok: structured error returned to the caller (classify UI) to surface.
    return { success: false, error: err.message };
  }
}

export async function deleteTask(recordId) {
  // 2026-04-20 audit — CLAUDE.md "Never delete user data" rule: previous
  // implementation ran DELETE which violated the soft-archive contract and
  // irreversibly removed audit trail + context for future AI training.
  // Now routes to soft-archive via `archived_at`. Callers that used to rely
  // on a hard delete (there should be none in the UI — restore is always
  // intended to work) now see the row disappear from list queries because
  // every list filter uses `.is('archived_at', null)`.
  try {
    const { error } = await supabase
      .from('tasks')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', recordId);
    if (error) return { success: false, error: error.message };
    return { success: true, archived: recordId };
  } catch (err) { return { success: false, error: err.message }; }
}

export async function archiveTask(taskId) {
  try {
    const { error } = await supabase
      .from('tasks')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', taskId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (err) { return { success: false, error: err.message }; }
}

export async function restoreTask(taskId) {
  const { error } = await supabase
    .from('tasks')
    .update({ archived_at: null })
    .eq('id', taskId);
  if (error) return { success: false, error: error.message };
  return { success: true };
}
